import React from 'react';
import loadable from '@loadable/component';
import { compose } from 'redux';
import * as queryString from 'query-string';
import removeTimeZoneFromDateString from '@justpark/helpers/src/removeTimeZoneFromDateString/removeTimeZoneFromDateString';
import setStatic from '../helpers/setStatic';
import { load as loadFormBootstrap } from '../stores/forms/seasonTickets';
import { loadListing } from '../stores/monthlyCheckout';
import { getConsents } from '../stores/consents';
import { load as loadAuth, logout } from '../stores/auth';
import { getDeleteCriteria } from '../stores/consents';
import { loadPartnerWhiteLabel } from '../stores/brand';
import DashboardPageWrapper from '../components/Dashboard/DashboardPageWrapper';
import { loadVehicles } from '../stores/vehicle';
import { loadPaymentMethods } from '../stores/payments';
import GET_LISTING_DETAILS_QUERY from '../queries/listingDetails/getListingDetailsQuery.graphql';
import GET_CHECKOUT_SIDEBAR_QUERY from '../queries/checkout/getCheckoutSidebarQuery.graphql';

const Loading = () => <div className="p-loading" />;
const DashboardPageWrapperLoading = () => (
  <DashboardPageWrapper modifier="primary-background" />
);

export const Home = loadable(
  () => import(/* webpackChunkName: "page-home" */ './Home'),
  { fallback: <Loading /> }
);

export const Landing = compose(
  setStatic('fetchData', (store, params) => {
    if (__SERVER__) {
      const {
        load: loadLocation,
        default: reducer
        // eslint-disable-next-line global-require
      } = require('../stores/locations');

      store.injectReducer(reducer);

      return store.dispatch(loadLocation(params.slug, params.country));
    }

    return Promise.resolve();
  })
)(
  loadable(() => import(/* webpackChunkName: "page-landing" */ './Landing'), {
    fallback: <Loading />
  })
);

export const SeasonTicket = compose(
  setStatic('fetchData', (store, params) =>
    store.dispatch(loadFormBootstrap(params.vendor))
  )
)(
  loadable(
    () => import(/* webpackChunkName: "page-season-ticket" */ './SeasonTicket'),
    {
      fallback: <Loading />
    }
  )
);

const redirectForTimeZoneDates = (
  queryParams,
  pathname,
  setStatusCodeValue
) => {
  const updatedParams = removeTimeZoneFromDateString(queryParams);

  if (updatedParams) {
    const newString = queryString.stringify(updatedParams);

    const httpStatus = {
      status: 302,
      url: `${pathname}?${newString}`
    };

    setStatusCodeValue(httpStatus);
  }
};

export const SearchResults = compose(
  setStatic(
    'fetchData',
    (
      store,
      params,
      queryParams,
      pathname,
      apolloClient,
      setStatusCodeValue
    ) => {
      redirectForTimeZoneDates(queryParams, pathname, setStatusCodeValue);
      return Promise.resolve();
    }
  )
)(
  loadable(
    () => import(/* webpackChunkName: "page-search-results" */ './Search'),
    {
      fallback: <Loading />
    }
  )
);

export const PartnerSearchResults = compose(
  setStatic(
    'fetchData',
    (
      store,
      params,
      queryParams,
      pathname,
      apolloClient,
      setStatusCodeValue
    ) => {
      const { partnerName } = params;

      redirectForTimeZoneDates(queryParams, pathname, setStatusCodeValue);

      return Promise.all([store.dispatch(loadPartnerWhiteLabel(partnerName))]);
    }
  )
)(
  loadable(
    () =>
      import(
        /* webpackChunkName: "page-search-results" */ './PartnerSearchResults'
      ),
    {
      fallback: <Loading />
    }
  )
);

export const LoginWall = loadable(
  () => import(/* webpackChunkName: "page-login-wall" */ './LoginWall'),
  {
    fallback: <Loading />
  }
);

export const Register = loadable(
  () => import(/* webpackChunkName: "page-register" */ './Register'),
  {
    fallback: <Loading />
  }
);

export const Checkout = compose(
  setStatic('fetchData', (store, params, query, pathname, apolloClient) => {
    const listingId = parseInt(params.listingId, 10);
    const { dispatch } = store;

    // Listing still used in brand store to check whitelabel
    const loadListingPromise = dispatch(loadListing(listingId));
    const loadVehiclesPromise = dispatch(loadVehicles());
    const loadPaymentsPromise = dispatch(loadPaymentMethods(listingId));

    const apolloPromise = apolloClient.query({
      query: GET_CHECKOUT_SIDEBAR_QUERY,
      variables: {
        listingId
      }
    });

    return Promise.all([
      apolloPromise,
      loadListingPromise,
      loadVehiclesPromise,
      loadPaymentsPromise
    ]);
  })
)(
  loadable(
    () =>
      import(
        /* webpackChunkName: "page-checkout" */ './checkout/MonthlyCheckout'
      ),
    {
      fallback: <Loading />
    }
  )
);

export const PartnerCheckout = compose(
  setStatic('fetchData', (store, params) => {
    const listingId = parseInt(params.listingId, 10);
    const { partnerName } = params;
    const { dispatch } = store;

    const whiteLabel = dispatch(loadPartnerWhiteLabel(partnerName));
    const listing = dispatch(loadListing(listingId));
    const loadVehiclesPromise = dispatch(loadVehicles());
    const loadPaymentsPromise = dispatch(loadPaymentMethods(listingId));

    return Promise.all([
      whiteLabel,
      listing,
      loadVehiclesPromise,
      loadPaymentsPromise
    ]);
  })
)(
  loadable(
    () =>
      import(
        /* webpackChunkName: "page-checkout" */ './checkout/PartnerCheckout'
      ),
    {
      fallback: <Loading />
    }
  )
);

export const ConsentsWall = compose(
  setStatic('fetchData', (store, params, queryParams, pathname) =>
    store.dispatch(getConsents(queryParams, false, pathname))
  )
)(
  loadable(
    () => import(/* webpackChunkName: "page-consent" */ './ConsentWall'),
    {
      fallback: <Loading />
    }
  )
);

export const AddListing = loadable(
  () =>
    import(/* webpackChunkName: "page-add-listing" */ './dashboard/AddListing'),
  {
    fallback: <Loading />
  }
);

export const EditListing = loadable(
  () =>
    import(
      /* webpackChunkName: "page-edit-listing" */ './dashboard/EditListing'
    ),
  {
    fallback: <Loading />
  }
);

export const UpdatePrompt = loadable(
  () =>
    import(
      /* webpackChunkName: "page-update-prompt" */ './dashboard/UpdatePrompt'
    ),
  {
    fallback: <Loading />
  }
);

export const ParkingSpaces = loadable(
  () =>
    import(
      /* webpackChunkName: "page-parking-spaces" */ './dashboard/ParkingSpaces'
    ),
  {
    fallback: <Loading />
  }
);

export const Availability = loadable(
  () =>
    import(
      /* webpackChunkName: "page-availability" */ './dashboard/Availability'
    ),
  {
    fallback: <Loading />
  }
);

export const VehiclePage = loadable(
  () =>
    import(/* webpackChunkName: "page-vehicle" */ './dashboard/VehiclePage'),
  {
    fallback: <Loading />
  }
);

export const PaymentsPage = loadable(
  () =>
    import(/* webpackChunkName: "page-payments" */ './dashboard/PaymentsPage'),
  {
    fallback: <Loading />
  }
);

export const WithdrawalPage = loadable(
  () =>
    import(
      /* webpackChunkName: "page-withdrawal" */ './dashboard/WithdrawalPage'
    ),
  {
    fallback: <Loading />
  }
);

export const BookingsMadePage = loadable(
  () =>
    import(
      /* webpackChunkName: "page-bookings-made" */ './dashboard/BookingsMadePage'
    ),
  {
    fallback: <DashboardPageWrapperLoading />
  }
);

export const SeasonTicketsDashboardPage = loadable(
  () => import('./dashboard/SeasonTicketsPage'),
  {
    fallback: <DashboardPageWrapperLoading />
  }
);

export const SeasonTicketDetailsPage = compose(
  setStatic('fetchData', (store, params) => {
    if (__SERVER__) {
      const {
        getSeasonTicketDetails,
        default: reducer
        // eslint-disable-next-line global-require
      } = require('../stores/seasonTicketDetails');

      store.injectReducer(reducer);

      const { id: seasonTicketId } = params;

      return store.dispatch(getSeasonTicketDetails(seasonTicketId));
    }

    return Promise.resolve();
  })
)(
  loadable(() => import('./dashboard/SeasonTicketDetailsPage'), {
    fallback: <DashboardPageWrapperLoading />
  })
);

export const BookingsDetailsPage = compose(
  setStatic('fetchData', (store, params) => {
    if (__SERVER__) {
      const {
        getBooking,
        default: reducer
        // eslint-disable-next-line global-require
      } = require('../stores/booking');

      store.injectReducer(reducer);

      const { bookingId } = params;

      return store.dispatch(getBooking(bookingId));
    }

    return Promise.resolve();
  })
)(
  loadable(
    () =>
      import(
        /* webpackChunkName: "page-booking-details" */ './dashboard/BookingDetailsPage'
      ),
    {
      fallback: <DashboardPageWrapperLoading />
    }
  )
);

export const ProfilePage = compose(
  setStatic('fetchData', (store) => {
    if (__SERVER__) {
      return store.dispatch(loadAuth()).then(() => {
        if (!store.getState().auth.user) {
          return null;
        }

        const {
          load: loadProfilePageData,
          default: reducer
          // eslint-disable-next-line global-require
        } = require('../stores/forms/profile');

        store.injectReducer(reducer);

        return Promise.all([
          store.dispatch(getConsents()),
          store.dispatch(loadProfilePageData())
        ]);
      });
    }

    return Promise.resolve();
  })
)(
  loadable(
    () =>
      import(
        /* webpackChunkName: "page-edit-profile" */ './dashboard/ProfilePage'
      ),
    {
      fallback: <DashboardPageWrapperLoading />
    }
  )
);

export const Logout = compose(
  setStatic('fetchData', (store) => store.dispatch(logout()))
)(
  loadable(() => import(/* webpackChunkName: "page-logout" */ './Logout'), {
    fallback: <Loading />
  })
);

export const Agreements = loadable(
  () =>
    import(
      /* webpackChunkName: "page-agreements" */ './policies/AgreementsPage'
    ),
  {
    fallback: <Loading />
  }
);

export const Cancellation = loadable(
  () =>
    import(
      /* webpackChunkName: "page-cancellation" */ './policies/CancellationPage'
    ),
  {
    fallback: <Loading />
  }
);

export const Privacy = loadable(
  () => import(/* webpackChunkName: "page-privacy" */ './policies/PrivacyPage'),
  {
    fallback: <Loading />
  }
);

export const Cookie = loadable(() => import('./policies/CookiePage'), {
  fallback: <Loading />
});

export const Terms = loadable(
  () => import(/* webpackChunkName: "page-terms" */ './policies/TermsPage'),
  {
    fallback: <Loading />
  }
);

export const BookingReceivedPage = loadable(
  () => import('./dashboard/BookingReceivedPage'),
  {
    fallback: <DashboardPageWrapperLoading />
  }
);

export const Insurance = loadable(
  () =>
    import(/* webpackChunkName: "page-insurance" */ './policies/InsurancePage'),
  {
    fallback: <Loading />
  }
);

export const ResetPassword = loadable(
  () => import(/* webpackChunkName: "page-reset-password" */ './ResetPassword'),
  {
    fallback: <Loading />
  }
);

export const PartnerHelp = loadable(() => import('./PartnerHelp'), {
  fallback: <Loading />
});

export const DeleteAccount = compose(
  setStatic('fetchData', (store) =>
    store.dispatch(loadAuth()).then(() => {
      if (!store.getState().auth.user) {
        return null;
      }
      return store.dispatch(getDeleteCriteria());
    })
  )
)(
  loadable(
    () =>
      import(/* webpackChunkName: "page-delete-account" */ './DeleteAccount'),
    {
      fallback: <Loading />
    }
  )
);

export const ListingPhotos = compose(
  setStatic('fetchData', (store, params) => {
    if (__SERVER__) {
      const {
        default: reducer,
        getListingUploads
        // eslint-disable-next-line global-require
      } = require('../stores/listingPhotos');

      store.injectReducer(reducer);

      const { listingId } = params;
      return store.dispatch(getListingUploads({ listingId }));
    }

    return Promise.resolve();
  })
)(
  loadable(() => import('./dashboard/ListingPhotosPage'), {
    fallback: <Loading />
  })
);

export const InsuranceWhileParkedWebview = compose(
  setStatic('fetchData', (store, params) => {
    if (__SERVER__) {
      const {
        getBookingPayments,
        default: bookingPaymentsReducer
        // eslint-disable-next-line global-require
      } = require('../stores/bookingPayments');

      const {
        getBooking,
        openInsuranceCheckoutModal,
        default: getBookingReducer
        // eslint-disable-next-line global-require
      } = require('../stores/booking');

      store.injectReducer([bookingPaymentsReducer, getBookingReducer]);

      const { bookingId } = params;

      return Promise.all([
        store.dispatch(getBooking(bookingId)),
        store.dispatch(getBookingPayments(bookingId))
      ]).then(() => store.dispatch(openInsuranceCheckoutModal()));
    }

    return Promise.resolve();
  })
)(
  loadable(() => import('./dashboard/InsuranceWhileParkedWebview'), {
    fallback: <Loading />
  })
);

export const BookingsReceived = compose(
  setStatic('fetchData', (store) => {
    // Ensure this code is only evaluated in the server side bundle so that
    // the client bundle does not contain the required dependency
    if (__SERVER__) {
      const {
        getBookingsReceived,
        default: reducer
        // eslint-disable-next-line global-require
      } = require('../stores/bookingsReceived');

      store.injectReducer(reducer);

      return store.dispatch(getBookingsReceived({ page: 1, filter: 'active' }));
    }

    return Promise.resolve();
  })
)(
  loadable(() => import('./dashboard/BookingsReceivedPage'), {
    fallback: <DashboardPageWrapperLoading />
  })
);

export const Dashboard = loadable(() => import('./dashboard/DashboardPage'), {
  fallback: <Loading />
});

export const EvSignupPage = loadable(() => import('./EvSignup'), {
  fallback: <Loading />
});

export const EvWelcomePage = loadable(() => import('./EvWelcome'), {
  fallback: <Loading />
});

export const EvEditPayment = loadable(() => import('./EvEditPayment'), {
  fallback: <Loading />
});

export const EvListingChargerDetails = loadable(
  () => import('./dashboard/EvListingChargerDetails'),
  {
    fallback: <DashboardPageWrapperLoading />
  }
);

export const ListingDetailsPage = compose(
  setStatic('fetchData', (store, params, query, pathname, apolloClient) => {
    /**
     * Using fetchData is not required in order for SSR to work with Apollo. We could remove this and see the same result
     * However, don't call this query here, then it won't get called until other fetchData request have been made
     * We want this request to run in parallel to the auth and feature flag requests
     * Running the query here will make them parallel and the rest cached in the SSR so it's not called twice
     */
    const { slug } = params;

    return apolloClient.query({
      query: GET_LISTING_DETAILS_QUERY,
      variables: {
        slug,
        reviewsOffset: 0
      }
    });
  })
)(
  loadable(
    () =>
      import(/* webpackChunkName: "page-listing-details" */ './ListingDetails'),
    { fallback: <Loading /> }
  )
);

export const SuperUserPage = loadable(
  () => import(/* webpackChunkName: "super-user" */ './SuperUser'),
  {
    fallback: <Loading />
  }
);

export const ListingOnboardingPage = compose(
  setStatic('fetchData', (store, params, query) => {
    if (__SERVER__ && query.listingId) {
      const {
        loadProspectiveListing,
        default: reducer
        // eslint-disable-next-line global-require
      } = require('../stores/listingOnboarding');

      store.injectReducer(reducer);

      return store.dispatch(loadProspectiveListing(query.listingId));
    }

    return Promise.resolve();
  })
)(
  loadable(
    () =>
      import(
        /* webpackChunkName: "start-listing-onboarding" */ './dashboard/ListingOnboarding'
      ),
    {
      fallback: <Loading />
    }
  )
);
